import React, { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { getUserById, updateUser } from "@/services/user.service";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import {
    FIRST_NAME_REQUIRED,
    LAST_NAME_REQUIRED,
    PROFILE_UPDATE_SUCCESS_MESSAGE,
    TRY_AGAIN_MESSAGE,
} from "@/utils/constants/messages";
import { getUserId } from "@/utils/functions/TokenFunction";
import { getEncrypted, setDataInLocalStorage } from "@/utils/functions/helperFunctions";
import { successToasterMessage } from "@/utils/functions/toaster";
import UpdateEmail from "@/components/modals/UpdateEmailModal";
import { ProfilePageSkeleton } from "@/components/skeletons";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ButtonLoadingContent, loadingButtonClass } from "@/components/buttons/ButtonLoadingContent";
import { Spinner } from "@/components/ui/spinner";

type ProfileSaveButtonProps = {
    id: string;
    loading: boolean;
    label: string;
};

function ProfileSaveButton({ id, loading, label }: ProfileSaveButtonProps) {
    return (
        <button
            id={id}
            type="submit"
            aria-busy={loading || undefined}
            className={loadingButtonClass(loading, "gs-profile-save-btn")}
        >
            <ButtonLoadingContent
                loading={loading}
                loaderId={`${id}-loader`}
                spinner={
                    <Spinner
                        id={`${id}-loader`}
                        size="sm"
                        className="gs-btn-loading-spinner absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white"
                    />
                }
            >
                {label}
            </ButtonLoadingContent>
        </button>
    );
}

type ProfileFieldRowProps = {
    label: string;
    labelId: string;
    children: React.ReactNode;
};

/** Prod layout: data-item > data-col > label + field(s) as flex siblings */
function ProfileFieldRow({ label, labelId, children }: ProfileFieldRowProps) {
    return (
        <div className="gs-profile-row">
            <div className="gs-profile-col">
                <span className="gs-profile-label" id={labelId}>
                    {label}
                </span>
                {children}
            </div>
        </div>
    );
}

function Profile() {
    const adminId = getUserId() ?? "";
    const [adminErrMsg, setAdminErrMsg] = useState("");
    const [profile, setProfile] = useState<any>();
    const [emailAddress, setEmailAddress] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [loading, setLoading] = useState(false);
    const [firstNameErrMsg, setFirstNameErrMsg] = useState("");
    const [lastNameErrMsg, setLastNameErrMsg] = useState("");
    const [showEditEmailModal, setShowEditEmailModal] = useState(false);

    const submitAdmin = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        const data = JSON.stringify({
            email_address: profile.email_address,
            first_name: firstName,
            last_name: lastName,
        });
        if (firstName.trim() === "" && lastName.trim() === "") {
            setLoading(false);
            setFirstNameErrMsg(FIRST_NAME_REQUIRED);
            setLastNameErrMsg(LAST_NAME_REQUIRED);
            return;
        }
        if (firstName.trim() === "") {
            setLoading(false);
            setFirstNameErrMsg(FIRST_NAME_REQUIRED);
            return;
        }
        if (lastName.trim() === "") {
            setLoading(false);
            setLastNameErrMsg(LAST_NAME_REQUIRED);
            return;
        }

        updateUser(
            adminId,
            data,
            (response: any) => {
                setLoading(false);
                if (response.status === 200) {
                    setProfile(response?.data);
                    setEmailAddress(response?.data.email_address);
                    setDataInLocalStorage("name", getEncrypted(firstName + " " + lastName));
                    successToasterMessage(PROFILE_UPDATE_SUCCESS_MESSAGE);
                    setAdminErrMsg("");
                } else {
                    setAdminErrMsg(TRY_AGAIN_MESSAGE);
                }
            },
            () => {
                setLoading(false);
                setAdminErrMsg(TRY_AGAIN_MESSAGE);
            }
        );
    };

    useEffect(() => {
        if (!adminId) {
            return;
        }
        getUserById(
            adminId,
            (response: any) => {
                setProfile(response?.data);
                setFirstName(response?.data.first_name);
                setLastName(response?.data.last_name);
                setEmailAddress(response?.data.email_address);
            },
            (err: any) => {
                console.log(err);
            }
        );
    }, [adminId]);

    const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFirstNameErrMsg("");
        setFirstName(event.target.value);
    };

    const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setLastNameErrMsg("");
        setLastName(event.target.value);
    };

    return (
        <>
            <Card className="gs-profile-card border-0 shadow-none">
                <div className="gs-profile-card-header">
                    <h4 className="gs-profile-title" id="my-profile-heading">
                        {HEADINGS.MY_PROFILE}
                    </h4>
                </div>
                <div className="gs-profile-card-body">
                    {profile ? (
                        <form onSubmit={submitAdmin}>
                            <div className="gs-profile-fields">
                                <ProfileFieldRow
                                    label={LABELS.EMAIL_ADDRESS}
                                    labelId="my-profile-email-address-label"
                                >
                                    <div className="gs-profile-email-field">
                                        <Input
                                            disabled
                                            type="text"
                                            id="my-profile-email-address-input"
                                            value={emailAddress}
                                            className="gs-profile-input"
                                        />
                                        <button
                                            type="button"
                                            className="gs-profile-edit-btn"
                                            onClick={() => setShowEditEmailModal(true)}
                                            aria-label="Edit email address"
                                        >
                                            <Pencil className="size-[1.125rem]" strokeWidth={1.75} />
                                        </button>
                                    </div>
                                </ProfileFieldRow>

                                <ProfileFieldRow
                                    label={LABELS.FIRST_NAME}
                                    labelId="my-profile-first-name-label"
                                >
                                    <div className="gs-profile-field-block">
                                        <Input
                                            id="my-profile-first-name-input"
                                            type="text"
                                            value={firstName}
                                            className="gs-profile-input"
                                            onChange={handleFirstNameChange}
                                        />
                                        {firstNameErrMsg && (
                                            <p
                                                id="my-profile-first-name-error-msg"
                                                className="gs-profile-field-error"
                                            >
                                                {firstNameErrMsg}
                                            </p>
                                        )}
                                    </div>
                                </ProfileFieldRow>

                                <ProfileFieldRow
                                    label={LABELS.LAST_NAME}
                                    labelId="my-profile-last-name-label"
                                >
                                    <div className="gs-profile-field-block">
                                        <Input
                                            id="my-profile-last-name-input"
                                            type="text"
                                            value={lastName}
                                            className="gs-profile-input"
                                            onChange={handleLastNameChange}
                                        />
                                        {lastNameErrMsg && (
                                            <p
                                                id="my-profile-last-name-error-msg"
                                                className="gs-profile-field-error"
                                            >
                                                {lastNameErrMsg}
                                            </p>
                                        )}
                                    </div>
                                </ProfileFieldRow>
                            </div>

                            {adminErrMsg && (
                                <p className="gs-profile-form-error" id="my-profile-error-msg">
                                    {adminErrMsg}
                                </p>
                            )}

                            <div className="gs-profile-actions">
                                <ProfileSaveButton
                                    id="my-profile-save-btn"
                                    loading={loading}
                                    label={BUTTONS.SAVE}
                                />
                            </div>
                        </form>
                    ) : (
                        <ProfilePageSkeleton />
                    )}
                </div>
            </Card>
            <UpdateEmail
                setEmailAddress={setEmailAddress}
                show={showEditEmailModal}
                onHide={() => setShowEditEmailModal(false)}
                setShowEditEmailModal={setShowEditEmailModal}
            />
        </>
    );
}

export default Profile;
